// Pure derivations for the extended (flagged) Behavior tab. Everything here is
// computed CLIENT-SIDE from counts we already fetch (shopping-behavior +
// insights) — no backend change. Kept pure + free of React
// so the branchy edge cases are unit-tested without a rendered component
// (mirrors `utils/behaviorSignals.ts`).
//
// Every rate returns `null` when it can't be computed honestly (zero/negative
// denominator or non-finite input) so callers never render a misleading number.

const isFiniteNum = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v)

// a / b * 100, rounded to 1 decimal. null when b <= 0 or either input is not a
// finite number, or when a < 0 (a rate can't be negative here).
export const rate = (
  numerator: number | null | undefined,
  denominator: number | null | undefined,
): number | null => {
  if (!isFiniteNum(numerator) || !isFiniteNum(denominator)) return null
  if (denominator <= 0 || numerator < 0) return null
  return Math.round((numerator / denominator) * 1000) / 10
}

// Mean = total / count. null when count <= 0 or inputs invalid.
export const mean = (
  total: number | null | undefined,
  count: number | null | undefined,
): number | null => {
  if (!isFiniteNum(total) || !isFiniteNum(count)) return null
  if (count <= 0) return null
  return total / count
}

// ── Named funnel/session derivations (thin wrappers for readability + tests) ──

export const conversionRate = (
  placedOrders: number | null | undefined,
  sessions: number | null | undefined,
): number | null => rate(placedOrders, sessions)

export const cartAbandonmentRate = (
  abandoned: number | null | undefined,
  cartsCreated: number | null | undefined,
): number | null => rate(abandoned, cartsCreated)

export const addToCartRate = (
  cartSessions: number | null | undefined,
  sessions: number | null | undefined,
): number | null => rate(cartSessions, sessions)

export const viewToCartRate = (
  addedToCart: number | null | undefined,
  productsViewed: number | null | undefined,
): number | null => rate(addedToCart, productsViewed)

export const viewToPurchaseRate = (
  purchased: number | null | undefined,
  productsViewed: number | null | undefined,
): number | null => rate(purchased, productsViewed)

// Checkouts started that didn't reach an order, as a % of checkouts started.
// null when `started` <= 0 or placed exceeds started (dirty data).
export const checkoutAbandonmentRate = (
  started: number | null | undefined,
  placed: number | null | undefined,
): number | null => {
  if (!isFiniteNum(started) || !isFiniteNum(placed)) return null
  if (started <= 0 || placed < 0 || placed > started) return null
  return rate(started - placed, started)
}

// ── Formatting ────────────────────────────────────────────────────────────────

// `43.5%`. Trims a trailing `.0` so whole numbers read `50%`, not `50.0%`.
export const formatPct = (value: number | null | undefined): string | null => {
  if (!isFiniteNum(value)) return null
  const rounded = Math.round(value * 10) / 10
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
  return `${text}%`
}
